/**
 * Editor Configuration
 * Defines variant configurations for the RichTextEditor component
 */

export const VARIANTS = {
  full: {
    name: 'full',
    showToolbar: true,
    minHeight: '10rem',
    toolbarButtons: [
      { group: 'format', buttons: ['bold', 'italic', 'strike', 'link'] },
      { group: 'heading', buttons: ['heading1', 'quote', 'code'] },
      { group: 'list', buttons: ['bullet', 'number'] },
      { group: 'media', buttons: ['embed', 'image'] }
    ],
    enableEmbeds: true,
    enableImages: true,
    placeholder: 'Write something...'
  },
  minimal: {
    name: 'minimal',
    showToolbar: true,
    minHeight: '6rem',
    toolbarButtons: [
      { group: 'format', buttons: ['bold', 'italic', 'link'] },
      { group: 'list', buttons: ['bullet', 'number'] }
    ],
    enableEmbeds: false,
    enableImages: true,
    placeholder: 'Write something...'
  },
  comment: {
    name: 'comment',
    showToolbar: true,
    minHeight: '80px',
    toolbarButtons: [
      { group: 'format', buttons: ['bold', 'italic', 'link'] },
      { group: 'list', buttons: ['bullet', 'number'] }
    ],
    enableEmbeds: false,
    enableImages: false,
    placeholder: 'What do you think?'
  },
  internal: {
    name: 'internal',
    showToolbar: true,
    minHeight: '80px',
    toolbarButtons: [
      { group: 'format', buttons: ['bold', 'italic', 'link'] },
      { group: 'list', buttons: ['bullet', 'number'] }
    ],
    enableEmbeds: false,
    enableImages: false,
    placeholder: 'Add a note for your team...'
  },
  description: {
    name: 'description',
    showToolbar: true,
    minHeight: '80px',
    toolbarButtons: [
      { group: 'format', buttons: ['bold', 'italic', 'link'] },
      { group: 'list', buttons: ['bullet'] }
    ],
    enableEmbeds: false,
    enableImages: false,
    placeholder: 'Add a page description...'
  }
};

export const TOOLBAR_BUTTONS = {
  bold: {
    id: 'bold',
    icon: 'bold',
    label: 'Bold',
    shortcut: '⌘B',
    shortcutKey: 'Mod-b',
    trixAttribute: 'bold'
  },
  italic: {
    id: 'italic',
    icon: 'italic',
    label: 'Italic',
    shortcut: '⌘I',
    shortcutKey: 'Mod-i',
    trixAttribute: 'italic'
  },
  strike: {
    id: 'strike',
    icon: 'strikethrough',
    label: 'Strikethrough',
    shortcut: '⌘⇧X',
    shortcutKey: 'Mod-Shift-x',
    trixAttribute: 'strike'
  },
  link: {
    id: 'link',
    icon: 'link',
    label: 'Link',
    shortcut: '⌘K',
    shortcutKey: 'Mod-k',
    trixAttribute: 'href',
    isDialog: true
  },
  heading1: {
    id: 'heading1',
    icon: 'heading',
    label: 'Heading',
    shortcut: '⌘⌥1',
    shortcutKey: 'Mod-Alt-1',
    trixAttribute: 'heading1'
  },
  quote: {
    id: 'quote',
    icon: 'quote',
    label: 'Quote',
    shortcut: '⌘⇧.',
    shortcutKey: 'Mod-Shift-.',
    trixAttribute: 'quote'
  },
  code: {
    id: 'code',
    icon: 'code',
    label: 'Code',
    shortcut: '⌘E',
    shortcutKey: 'Mod-e',
    trixAttribute: 'code'
  },
  bullet: {
    id: 'bullet',
    icon: 'list-bullet',
    label: 'Bullet List',
    shortcut: '⌘⇧8',
    shortcutKey: 'Mod-Shift-8',
    trixAttribute: 'bullet'
  },
  number: {
    id: 'number',
    icon: 'list-number',
    label: 'Numbered List',
    shortcut: '⌘⇧7',
    shortcutKey: 'Mod-Shift-7',
    trixAttribute: 'number'
  },
  embed: {
    id: 'embed',
    icon: 'video',
    label: 'Embed',
    shortcut: null,
    isAction: true,
    action: 'insertEmbed'
  },
  image: {
    id: 'image',
    icon: 'photo',
    label: 'Image',
    shortcut: null,
    isAction: true,
    action: 'insertImage'
  }
};

export const IMAGE_CONFIG = {
  maxSize: 10 * 1024 * 1024, // 10MB
  maxSizeFormatted: '10MB',
  allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  blockedTypes: ['image/svg+xml'],
  allowedExtensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp']
};

export const MARKDOWN_SHORTCUTS = [
  // Bold: **text** or __text__
  { pattern: /\*\*([^*]+)\*\*$/, attribute: 'bold' },
  { pattern: /__([^_]+)__$/, attribute: 'bold' },
  // Italic: *text* or _text_
  { pattern: /(?<!\*)\*([^*]+)\*(?!\*)$/, attribute: 'italic' },
  { pattern: /(?<!_)_([^_]+)_(?!_)$/, attribute: 'italic' },
  // Strikethrough: ~~text~~
  { pattern: /~~([^~]+)~~$/, attribute: 'strike' },
  // Inline code: `code`
  { pattern: /`([^`]+)`$/, attribute: 'code' },
  // Bullet list: - or * at line start
  { pattern: /^[-*]\s$/, blockAttribute: 'bullet', clearText: true },
  // Numbered list: 1. at line start
  { pattern: /^\d+\.\s$/, blockAttribute: 'number', clearText: true },
  // Quote: > at line start
  { pattern: /^>\s$/, blockAttribute: 'quote', clearText: true },
  // Heading: # at line start
  { pattern: /^#\s$/, blockAttribute: 'heading1', clearText: true }
];

export function getVariantConfig(variant) {
  return VARIANTS[variant] || VARIANTS.minimal;
}

export function getToolbarButton(buttonId) {
  return TOOLBAR_BUTTONS[buttonId];
}

export default {
  VARIANTS,
  TOOLBAR_BUTTONS,
  IMAGE_CONFIG,
  MARKDOWN_SHORTCUTS,
  getVariantConfig,
  getToolbarButton
};
